import type { ComponentProps } from 'react';
import { PATHS, template } from '@belong/common';
import { Image, Link, Text } from '@belong/ui';
import clsx from 'clsx';
import { getCdnListingSrc } from 'src/utils/get-cdn-listing-src';
import css from './neighborhood-cover.module.css';
import { NEIGHBORHOOD_COVER_STRINGS } from './neighborhood-cover.strings';

type NeighborhoodCoverProps = Pick<ComponentProps<typeof Image>, 'src'> & {
  neighborhoodName: string;
  // Optional, like `CityCover`'s: the render below already guards it.
  phoneNumber?: string;
  state: string;
};

export function NeighborhoodCover({
  neighborhoodName,
  phoneNumber,
  src,
  state,
}: NeighborhoodCoverProps): JSX.Element {
  return (
    <div
      className="bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url("${getCdnListingSrc(src)}")` }}
    >
      <div
        className={clsx(
          'py-4xl flex flex-col items-center text-center',
          css.coverContainer,
        )}
      >
        <div className="py-xl md:pb-2xl md:pt-3xl z-10 m-auto w-full md:mx-auto">
          <Text
            as="h2"
            variant="h1"
            fontWeight="semibold"
            className="mb-sm hidden whitespace-pre-line text-center text-white md:mx-auto md:block"
          >
            {template(NEIGHBORHOOD_COVER_STRINGS['title'], {
              neighborhoodName,
              state,
            })}
          </Text>
          <Text
            as="h2"
            variant="h2"
            fontWeight="semibold"
            className="mb-sm px-xs md:mb-xl block whitespace-pre-line text-center text-white md:hidden md:px-0"
          >
            {template(NEIGHBORHOOD_COVER_STRINGS['title'], {
              neighborhoodName,
              state,
            })}
          </Text>
          <div
            className={clsx(
              'mt-md relative mx-auto md:mt-0 md:flex md:items-center md:justify-center',
              css.bannerContainer,
            )}
          >
            <Text className={css.neonText}>
              We&apos;ll make sure it&apos;s loved.
            </Text>
          </div>
          <div
            className={clsx(
              'mt-2xl px-2sm md:mt-2xl flex w-full justify-center md:mx-auto',
            )}
          >
            <Link
              variant="solid"
              size="large"
              href={PATHS.HOMEOWNER_ONBOARDING_PROPERTY_ADDRESS}
              className={css.cta1}
            >
              {NEIGHBORHOOD_COVER_STRINGS.seeIfQualify}
            </Link>
          </div>
          {phoneNumber && (
            <div className="mt-xl px-2sm flex w-full justify-center">
              <span>
                <Text fontWeight="semibold" as="span" className="text-white">
                  {NEIGHBORHOOD_COVER_STRINGS.orCall}
                </Text>{' '}
                <Link href={`tel:+1${phoneNumber}`} buttonType="secondary">
                  {phoneNumber}
                </Link>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
